import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GRID_NUMBER_CHECKBOX, LEDGER, NUMBER, PERCENTAGE } from 'common/constants/gridType';
import { GET_ENTER_DATA } from 'common/constants/inputs';
import { Units } from 'common/types/format';
import RowLabelTooltip from 'components/RowLabelTooltip/RowLabelTooltip';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { useRevealEllipsisInTooltip } from 'services/hooks';
import { formatNumbers, toString } from 'utilities';
import { useWaterfallFXContext } from '../WaterfallFXContext/WaterfallFXContext';

interface WaterfallFXValueViewerProps {
  value?: any;
  cell: ICell;
  selected?: boolean;
  onDoubleClick?: (event: React.MouseEvent) => void;
  setDoubleClicked?: (value: boolean) => void;
}

const WaterfallFXValueViewer: React.FC<WaterfallFXValueViewerProps> = ({
  cell,
  selected,
  onDoubleClick,
  setDoubleClicked,
}) => {
  const { tooltipRef, width } = useRevealEllipsisInTooltip();
  const waterfallFXContext = useWaterfallFXContext();

  const keyUpHandler = useCallback(
    (event: KeyboardEvent) => {
      const key = event.key || event.keyCode;
      if (selected && key && key.toString().toUpperCase() === 'F2') {
        setDoubleClicked?.(true);
        onDoubleClick?.(event as unknown as React.MouseEvent);
      }
    },
    [onDoubleClick, selected, setDoubleClicked]
  );

  const contextFormat = useMemo(() => {
    const selectedCurrency = waterfallFXContext?.selectedCurrency;
    const customFormat = cell.customFormat as { currency?: { units?: Units } } | null | undefined;
    return {
      currency: {
        sourceCurrency: selectedCurrency?.sourceCurrency,
        symbol: selectedCurrency?.symbol,
        additionalCurrencies: waterfallFXContext?.currencyList,
        code: selectedCurrency?.sourceCurrency,
        units: customFormat?.currency?.units ?? waterfallFXContext?.units,
      },
    };
  }, [cell.customFormat, waterfallFXContext]);

  const gridTypeHandlers = useMemo(
    () => [
      {
        canHandle: (currentCell: ICell) =>
          [NUMBER, PERCENTAGE, GRID_NUMBER_CHECKBOX].includes(currentCell.gridType as string) &&
          !Number.isNaN(Number(currentCell.value)),
        getValue: (currentCell: ICell) => {
          if (!currentCell.readOnly && !toString(currentCell.value).length) {
            return currentCell.enterDataMessage ?? GET_ENTER_DATA(currentCell.isRequired, currentCell.enterDataMessage);
          }
          if (currentCell.readOnly && currentCell.allowEmptyValue && !toString(currentCell.value).length) {
            return '';
          }
          const mergedCurrencyFormat = {
            currency: {
              ...contextFormat?.currency,
            },
          } as any;
          return formatNumbers({
            ...currentCell,
            ...mergedCurrencyFormat,
          });
        },
      },
      {
        canHandle: (currentCell: ICell) => currentCell.gridType === LEDGER,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        getValue: (currentCell: ICell) => 0,
      },
    ],
    [contextFormat]
  );

  const calcValue = useCallback(() => {
    if (cell.hidden) {
      return '';
    }
    const handler = gridTypeHandlers.find(h => h.canHandle(cell));
    if (handler) {
      return handler.getValue(cell);
    }
    if (!cell.readOnly && !cell.hidden && !cell.value && cell.placeholder) {
      return cell.placeholder;
    }
    return cell.value;
  }, [cell, gridTypeHandlers]);

  const [value, setValue] = useState<any>();

  const getValue = useCallback(() => {
    setValue(calcValue());
  }, [calcValue]);

  useEffect(() => {
    document.addEventListener('keyup', keyUpHandler);
    return () => document.removeEventListener('keyup', keyUpHandler);
  }, [keyUpHandler]);

  useEffect(() => {
    getValue();
  }, [cell.value, cell.currency, cell.suffix, cell.hidden, getValue]);

  const spanId = useMemo(() => {
    if (cell.extraAttr) {
      return `${cell.extraAttr}-${cell.alias}-${cell.key}_viewer`;
    }

    return `${cell.alias}-${cell.key}_viewer`;
  }, [cell]);

  const valueOrNA = useMemo(() => (cell.value !== null ? value : 'N/A'), [cell, value]);

  if (
    cell.className?.includes('table-header') ||
    cell.className?.includes('header-no-border') ||
    cell.className?.includes('show-tooltip')
  ) {
    return (
      <span className="value-viewer" ref={tooltipRef}>
        {value ? (
          <RowLabelTooltip title={value} containerWidth={width}>
            <span id={`${cell.alias}-${cell.key}`} className="text-cell">
              {value}
            </span>
          </RowLabelTooltip>
        ) : (
          <span id={`${cell.alias}-${cell.key}`} className="text-cell">
            {value}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className="value-viewer">
      <span id={spanId} className={`text-cell ${cell.className ?? ''}`}>
        {!cell.displayNAforNull ? value : valueOrNA}
      </span>
    </span>
  );
};

export default WaterfallFXValueViewer;
